import React, { useEffect, useState } from "react";
import placeholderImg from "../../assets/images/placeholder.png";
import { useParams, Link, useNavigate } from "react-router-dom";
import {
  CalendarDays,
  HandCoins,
  Languages,
  LibraryBig,
  MailOpen,
  BookOpen,
  Globe,
  CircleArrowLeft,
  ChevronsDown,
  BadgeDollarSign,
} from "lucide-react";
import InfoCard from "./InfoCard";
import GeneralCard from "./GeneralCard";
import Sidebar from "../Universities/Sidebar";
import { EntryRequirementDetailData } from "../../Data/courseData";
import CourseContent from "./CourseContent/CourseContent";
import EntryRequirementDetails from "./GeneralCard/EntryRequirementDetail";
import ScholarshipDetail from "./GeneralCard/ScholarshipDetail";
import EnquireSidebar from "./EnquireSidebar";
import { useCourses } from "../../context/CourseContext";
import { useUniversities } from "../../context/UniversityContext";
import { db } from "../../firebase";
import { collection, query, where, getDocs, limit } from "firebase/firestore";
import {
  Course,
  EntryRequirement,
  Scholarship,
  Deal,
  EnglishRequirement,
} from "../../types/Course";

export default function CourseTemplate() {
  const { slug } = useParams();
  const { loading: metadataLoading, error: contextError } = useCourses();
  const {
    universities,
    loading: unisLoading,
    error: unisError,
  } = useUniversities();

  const [course, setCourse] = useState<Course | null>(null);
  const [courseLoading, setCourseLoading] = useState(true);
  const [courseError, setCourseError] = useState<string | null>(null);
  const [uniLogo, setUniLogo] = useState<string>(placeholderImg);
  const navigate = useNavigate();

  // const handleBackToCourses = () => {
  //   if (!course?.fieldOfStudy) return;
  //   navigate("/courses", {
  //     state: { preselectedFieldOfStudy: course.fieldOfStudy },
  //   });
  // };

  const handleBackToCourses = () => {
    if (!course?.fieldOfStudy) return;
    navigate("/courses", {
      state: { preselectedFieldOfStudy: course.fieldOfStudySlug }, // or whatever your field is
    });
  };
  // Load specific course from Firestore
  useEffect(() => {
    const fetchSpecificCourse = async () => {
      if (!slug) return;
      try {
        setCourseLoading(true);
        const q = query(
          collection(db, "course-listing"),
          where("courseSlug", "==", slug),
          limit(1),
        );
        const querySnapshot = await getDocs(q);

        if (!querySnapshot.empty) {
          const foundCourse = querySnapshot.docs[0].data() as Course;
          setCourse(foundCourse);

          // Find Uni Logo
          const foundUni = universities.find(
            (u) => u.slug === foundCourse.uniSlug,
          );
          if (foundUni?.logo) {
            try {
              const resolvedLogo = new URL(
                `../../assets/images/${foundUni.logo}`,
                import.meta.url,
              ).href;
              setUniLogo(resolvedLogo);
            } catch (err) {
              setUniLogo(placeholderImg);
            }
          } else {
            setUniLogo(placeholderImg);
          }
        } else {
          setCourse(null);
        }
      } catch (err) {
        console.error("Error fetching course:", err);
        setCourseError("Failed to load course details");
      } finally {
        setCourseLoading(false);
      }
    };

    if (!unisLoading) {
      fetchSpecificCourse();
    }
  }, [slug, universities, unisLoading]);

  // Local states for qualification/scholarship expand
  const [selectedQualificationCard, setSelectedQualificationCard] = useState<
    string | null
  >(null);
  const [selectedScholarshipCard, setSelectedScholarshipCard] = useState<
    string | null
  >(null);

  const handleQualificationCardClick = (cardId: string) =>
    setSelectedQualificationCard(
      selectedQualificationCard === cardId ? null : cardId,
    );

  const handleScholarshipCardClick = (cardId: string) =>
    setSelectedScholarshipCard(
      selectedScholarshipCard === cardId ? null : cardId,
    );

  // Total Deals handle
  // const totalDeal = course?.deals.reduce(
  //   (sum, deal) => sum + (deal.amount || 0),
  //   0
  // );

  // Total statistics handles (Note: These will be 0 or 1 now since we aren't loading all courses anymore)
  // To restore these, we would need separate count queries
  const totalDeal = Object.values(course?.deals || {}).reduce(
    (sum, deal) => sum + (Number(deal.amount) || 0),
    0,
  );

  const totalUniversities = 1; // Placeholder: needs a count query
  const totalScholarships = Object.keys(course?.scholarships || {}).length; // Placeholder

  // Loading state
  const isLoading = courseLoading || unisLoading;
  if (isLoading) {
    return (
      <div className="lg:w-[1024px] flex mx-auto gap-8">
        <div className="flex flex-col gap-10 md:gap-24 my-10 items-center mx-6 w-full lg:w-3/4">
          <div className="flex items-center justify-center h-64">
            <div className="text-lg">Loading course information...</div>
          </div>
        </div>
      </div>
    );
  }

  // Error state
  const error = courseError || unisError || contextError;
  if (error) {
    return (
      <div className="lg:w-[1024px] flex mx-auto gap-8">
        <div className="flex flex-col gap-10 md:gap-24 my-10 items-center mx-6 w-full lg:w-3/4">
          <div className="flex flex-col items-center justify-center h-64 gap-4">
            <div className="text-lg text-red-600">
              {coursesError || unisError}
            </div>
            <Link to="/courses" className="text-blue-600 hover:underline">
              ← Back to Courses
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // No course found
  if (!course) {
    return (
      <div className="lg:w-[1024px] flex mx-auto gap-8">
        <div className="flex flex-col gap-10 md:gap-24 my-10 items-center mx-6 w-full lg:w-3/4">
          <div className="flex flex-col items-center justify-center h-64 gap-4">
            <div className="text-lg">Course not found</div>
            <Link to="/courses" className="text-blue-600 hover:underline">
              ← Back to Courses
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const entryRequirementArr: EntryRequirement[] =
    course.entryRequirements || [];
  // const scholarshipArr: Scholarship[] = course.scholarships || [];
  const scholarshipArr: Scholarship[] = Object.values(
    course.scholarships || {},
  );
  // const dealsArr: Deal[] = course.deals || [];
  const dealsArr: Deal[] = Object.values(course.deals || {});

  return (
    <div className="lg:w-[1024px] flex mx-auto">
      <div className="flex flex-col gap-10 md:gap-24 my-24 items-center mx-6 w-full lg:w-3/4">
        {/* Back Button */}
        <div className="flex flex-col gap-4 w-full">
          <div className="w-full">
            <Link
              to="/courses"
              className="flex gap-1 items-center text-amber-400 text-lg px-4"
            >
              <CircleArrowLeft />
              Back to Courses
            </Link>
          </div>
          <h2 className="flex font-bold md:hidden">{course.courseName}</h2>
          <div className="flex gap-4 items-center">
            {uniLogo !== placeholderImg && (
              <div className="flex items-center justify-center w-36 h-36 lg:w-44 lg:h-44 shadow-lg p-2">
                <img src={uniLogo} alt="" />
              </div>
            )}
            <div className="flex flex-col gap-2">
              <h2 className="hidden md:flex text-2xl font-bold">
                {course.courseName}
              </h2>
              <button
                onClick={handleBackToCourses}
                className="border text-sm md:text-base px-2 py-0.5 md:px-4 md:py-1 border-amber-400 w-fit rounded-2xl hover:bg-amber-400 duration-300 text-amber-400 hover:text-white cursor-pointer"
              >
                {course.fieldOfStudy}
              </button>
              <Link
                to={course.uniSlug ? `/university/${course.uniSlug}` : "#"}
                className="hover:underline text-xs md:text-base"
              >
                {course.universityName} | {course.country}
              </Link>
            </div>
          </div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 place-items-center w-full">
          <InfoCard icon={CalendarDays} title={"Intake"} desc={course.intake} />
          <InfoCard
            icon={BookOpen}
            title={"Course Duration"}
            desc={course.duration + " years"}
          />
          {course.abroadDuration != "0" && (
            <InfoCard
              icon={Globe}
              title={"Overseas Transfer"}
              desc={
                <div className="flex flex-col items-center justify-center">
                  <p>{course.overseasTransfer}</p>
                  <p>{course.abroadDuration} years</p>
                </div>
              }
            />
          )}
          {/* {InfoCardData.map((item) => (
            <InfoCard icon={item.icon} title={item.title} desc={item.desc} />
          ))} */}
        </div>

        {/* Application Steps  */}
        <div className="flex flex-col gap-4 w-full">
          <h3 className="text-lg md:text-4xl font-bold ">
            Is this course for you?
          </h3>
          <span className="w-full border-b border-black" />
          <p className="md:text-lg">
            {2 + (scholarshipArr.length > 0 ? 1 : 0)}-steps checklist if this
            course is a match for you
          </p>
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex flex-col gap-4 md:w-2/3">
              {/* Step 1 */}
              <button
                onClick={() => {
                  const el = document.getElementById("entry-requirements");
                  if (el) el.scrollIntoView({ behavior: "smooth" });
                }}
                className="flex justify-between items-center w-full p-2 md:p-4 rounded-lg border border-gray-500 shadow shadow-gray-500 cursor-pointer"
              >
                <div className="flex flex-col text-left gap-2">
                  <div className="flex items-center gap-4">
                    <p className="bg-amber-400 text-white p-1 w-16 md:w-20 text-center rounded-r-full text-sm">
                      Step 1
                    </p>
                    <p className="font-bold text-sm md:text-base">
                      Check Entry Requirement
                    </p>
                  </div>
                  <div className="flex gap-2 text-sm ">
                    {entryRequirementArr.map((item) => (
                      <div key={`req-${item.qualification}`}>
                        {item.qualification}
                      </div>
                    ))}
                  </div>
                </div>
                <div className="flex items-center hover:underline text-sm md:text-base">
                  Details <ChevronsDown className="h-5 w-5" />
                </div>
              </button>

              {/* step 2 */}
              <button
                onClick={() => {
                  const el = document.getElementById("fees");
                  if (el) el.scrollIntoView({ behavior: "smooth" });
                }}
                className="flex justify-between items-center w-full p-2 md:p-4 rounded-lg border border-gray-500 shadow shadow-gray-500 cursor-pointer"
              >
                <div className="flex flex-col text-left gap-2">
                  <div className="flex items-center gap-4">
                    <p className="bg-amber-400 text-white p-1 w-16 md:w-20 text-center rounded-r-full text-sm">
                      Step 2
                    </p>
                    <p className="font-bold text-sm md:text-base">
                      Check Course Fees
                    </p>
                  </div>
                  <div className="flex gap-2 text-sm ">
                    <div>
                      Estimated fees is{" "}
                      <span className="font-bold">
                        {/* MYR {course.tuitionFee.toLocaleString()} */}
                        MYR{" "}
                        {(course.tuitionFee + course.registrationFee)
                          // course.resourceFee
                          .toLocaleString()}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="flex items-center hover:underline text-sm md:text-base">
                  Details <ChevronsDown className="h-5 w-5" />
                </div>
              </button>

              {/* step 3 */}
              {scholarshipArr.length > 0 && (
                <button
                  onClick={(e) => {
                    e.preventDefault(); // prevent any default scroll/jump
                    const el = document.getElementById("scholarships");
                    if (el) {
                      el.scrollIntoView({ behavior: "smooth" });
                    }
                    // if not found → do nothing, scroll stays where it is
                  }}
                  className="flex justify-between items-center w-full p-2 md:p-4 rounded-lg border border-gray-500 shadow shadow-black"
                >
                  <div className="flex flex-col text-left gap-2">
                    <div className="flex items-center gap-4">
                      <p className="bg-amber-400 text-white p-1 w-16 md:w-20 text-center rounded-r-full text-sm">
                        Step 3
                      </p>
                      <p className="font-bold text-sm md:text-base">
                        Check Nett Fee with Scholarships
                      </p>
                    </div>
                    <div className="flex gap-2 text-sm ">
                      Scholarships available:{" "}
                      {/* <span className="font-bold">
                      {course.scholarships.length} Awards
                    </span> */}
                      <span className="font-bold">
                        {Object.keys(course.scholarships || {}).length} Awards
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center hover:underline text-sm md:text-base">
                    Details <ChevronsDown className="h-5 w-5" />
                  </div>
                </button>
              )}

              <button
                onClick={() => {
                  const el = document.getElementById("entry-requirements");
                  if (el) el.scrollIntoView({ behavior: "smooth" });
                }}
                className="flex justify-between items-center w-full "
              >
                <div className="flex flex-col text-left gap-2">
                  <div className="flex items-center gap-4">
                    <p className="flex items-center justify-center bg-amber-400 text-white w-12 h-12 rounded-full text-xs">
                      Step 1
                    </p>
                    <p className="font-bold text-sm md:text-base">
                      Check Entry Requirement
                    </p>
                  </div>
                </div>
                <div className="flex items-center hover:underline text-sm md:text-base cursor-pointer">
                  Details <ChevronsDown className="h-5 w-5" />
                </div>
              </button>
              <button
                onClick={() => {
                  const el = document.getElementById("fees");
                  if (el) el.scrollIntoView({ behavior: "smooth" });
                }}
                className="flex justify-between items-center w-full "
              >
                <div className="flex flex-col text-left gap-2">
                  <div className="flex items-center gap-4">
                    <p className="flex items-center justify-center bg-amber-400 text-white w-12 h-12 rounded-full text-xs">
                      Step 2
                    </p>
                    <p className="font-bold text-sm md:text-base">
                      Check Course Fees
                    </p>
                  </div>
                </div>
                <div className="flex items-center hover:underline text-sm md:text-base cursor-pointer">
                  Details <ChevronsDown className="h-5 w-5" />
                </div>
              </button>
              <button
                onClick={() => {
                  const el = document.getElementById("scholarships");
                  if (el) el.scrollIntoView({ behavior: "smooth" });
                }}
                className="flex justify-between items-center w-full "
              >
                <div className="flex flex-col text-left gap-2">
                  <div className="flex items-center gap-4">
                    <p className="flex items-center justify-center bg-amber-400 text-white w-12 h-12 rounded-full text-xs">
                      Step 3
                    </p>
                    <p className="font-bold text-sm md:text-base">
                      Check Scholarships
                    </p>
                  </div>
                </div>
                <div className="flex items-center hover:underline text-sm md:text-base cursor-pointer">
                  Details <ChevronsDown className="h-5 w-5" />
                </div>
              </button>

              <button
                onClick={() => {
                  const el = document.getElementById("consultation");
                  if (el) el.scrollIntoView({ behavior: "smooth" });
                }}
                className="flex justify-between items-center w-full "
              >
                <div className="flex flex-col text-left gap-2">
                  <div className="flex items-center gap-4">
                    <p className="flex items-center justify-center bg-amber-400 text-white w-12 h-12 rounded-full text-xs">
                      Step 4
                    </p>
                    <p className="font-bold text-sm md:text-base">
                      Consult with our education consultant
                    </p>
                  </div>
                </div>
                <div className="flex items-center hover:underline text-sm md:text-base cursor-pointer">
                  Details <ChevronsDown className="h-5 w-5" />
                </div>
              </button>
            </div>

            {/* step 4 */}
            {/* <div className="flex flex-col items-center justify-center md:w-1/3 p-2 md:p-4 rounded-lg border border-gray-500 shadow shadow-black">
              <div className="flex flex-col md:gap-2">
                <div className="flex items-center gap-4">
                  <p className="bg-amber-400 text-white p-1 w-16 md:w-20 text-center rounded-r-full text-sm">
                    Step 4
                  </p>
                  <p className="font-bold text-sm">
                    Determine your best options
                  </p>
                </div>
                <div className="flex items-center">
                  <p className="w-3/4 md:w-full text-sm">
                    Compare {course.fieldOfStudy} courses & estimated total fee
                    across {totalUniversities} institutions with{" "}
                    {totalScholarships} awards
                  </p>
                  <CalendarDays className="w-20 h-20 md:hidden" />
                </div>
              </div>
              <CalendarDays className="hidden md:flex w-44 h-44" />
              <button className="bg-amber-400 p-2 md:p-4 rounded text-white mt-2">
                Start Pathway Match
              </button>
            </div> */}
          </div>
        </div>

        {/* Qualifications  */}
        <div
          id="entry-requirements"
          className="flex flex-col gap-4 scroll-mt-28 w-full"
        >
          <h3 className="text-lg md:text-4xl font-bold">Entry Requirements</h3>
          <span className="w-full border-b border-black" />

          <p className="md:text-lg">Click on qualifications to see details</p>
          <div className="flex flex-col lg:flex-row gap-4">
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-3 gap-4 lg:w-2/3">
              {entryRequirementArr.map((item, index) => (
                <div
                  key={`entry-${item.qualification || index}`}
                  className={`transition-all duration-300 ${
                    selectedQualificationCard &&
                    selectedQualificationCard !== `entry-${index}`
                      ? "hidden"
                      : ""
                  }`}
                >
                  <GeneralCard
                    title={item.qualification}
                    desc={item.grade}
                    isSelected={selectedQualificationCard === `entry-${index}`}
                    onCardClick={() =>
                      handleQualificationCardClick(`entry-${index}`)
                    }
                    showDetailsSection={true}
                    detailsContent={
                      <EntryRequirementDetails
                        // title={EntryRequirementDetailData[index]?.title}
                        desc={course.entryDetails}
                        // desc={EntryRequirementDetailData[index]?.desc}
                        title={item.qualification}
                        grade={item.grade}
                        details={item.details}
                        interview={course.interview}
                      />
                    }
                  />
                </div>
              ))}
              {!selectedQualificationCard && (
                <GeneralCard
                  title={"Other Qualification"}
                  desc={"Contact our counsellor"}
                  isSelected={false}
                  onCardClick={() =>
                    navigate("/form", { state: { formType: "consultation" } })
                  }
                  showDetailsSection={false}
                  detailsContent={false}
                />
              )}
            </div>
            {/* <div className="flex items-center justify-center shadow shadow-black lg:w-1/3 p-5 rounded-lg">
              <HandCoins className="w-1/2 h-1/2 md:w-1/3 md:h-1/3 lg:w-1/2 lg:h-1/2" />
              <div className="flex flex-col gap-2 w-1/2 md:w-2/3 lg:w-1/2">
                <p className="font-bold">Instant Results Check</p>
                <p>
                  Check your eligibility for similar courses across 60
                  institutions with Pathway Match!
                </p>
                <button className="bg-amber-400 p-2 text-white rounded">
                  Start Now
                </button>
              </div>
            </div> */}
          </div>

          {Array.isArray(course.englishRequirements) &&
            course.englishRequirements.length > 0 && (
              <div className="flex items-center gap-10 p-4 shadow shadow-black w-fit">
                <Languages className="w-20 h-20" />
                <div>
                  <div className="font-semibold">English Requirement</div>
                  <ul className="text-sm md:text-base">
                    {course.englishRequirements.map((item, index) => (
                      <li key={index}>
                        {item.title}: {item.details}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            )}
        </div>

        {/* Course Fees  */}
        <div id="fees" className="flex flex-col gap-4 w-full scroll-mt-28">
          <h3 className="text-lg md:text-4xl font-bold">Course Fees</h3>
          <span className="w-full border-b border-black" />
          <p>Estimated total fees for this course</p>
          {/* <button className="bg-amber-400 text-white p-2 w-fit rounded-lg">
            Domestic
          </button> */}
          <div className="flex flex-col md:flex-row gap-2">
            <div className="flex flex-col gap-2 md:w-1/2">
              <div className="flex justify-between border border-gray-500 p-2 shadow rounded-lg">
                <p>Registration Fee</p>
                <p>
                  MYR {(Number(course.registrationFee) || 0).toLocaleString()}
                </p>
              </div>
              <div className="flex justify-between border border-gray-500 p-2 shadow rounded-lg">
                <p>Resource Fee</p>
                <p>MYR {(Number(course.resourceFee) || 0).toLocaleString()}</p>
              </div>
              <div className="flex justify-between border border-gray-500 p-2 shadow rounded-lg">
                <p>Tuition Fee</p>
                <p>MYR {(Number(course.tuitionFee) || 0).toLocaleString()}</p>
              </div>
              <div className="flex justify-between font-bold border border-gray-500 p-2 shadow rounded-lg bg-amber-100">
                <p>Estimated Total Fees</p>
                <p>
                  MYR{" "}
                  {(
                    (Number(course.tuitionFee) || 0) +
                    (Number(course.registrationFee) || 0) +
                    (Number(course.resourceFee) || 0)
                  )
                    // course.resourceFee
                    .toLocaleString()}
                </p>
              </div>

              {/* <div className="md:w-1/2 bg-gray-300 flex items-center justify-center p-2 rounded-2xl h-fit">
              <div className="w-2/3">
                <p>Deposit</p>
                <p>Library Deposit @ MYR 500 / Once-off</p>
              </div>
              <HandCoins className="w-20 h-20" />
            </div> */}

              {Array.isArray(course.extraFees) &&
                course.extraFees.length > 0 && (
                  <div className="md:w-1/2 bg-gray-300 flex items-center justify-center p-4 rounded-2xl h-fit">
                    <div className="w-2/3">
                      <p className="font-semibold">Extra Fees</p>
                      {course.extraFees.map((item) => (
                        <p>
                          {item.title} @ MYR {item.amount}
                        </p>
                      ))}
                    </div>
                    <HandCoins className="w-20 h-20" />
                  </div>
                )}
            </div>
          </div>

          {/* Scholarship  */}
          {/* {(course.scholarships.length || course.deals.length) > 0 && ( */}
          {(Object.keys(course.scholarships || {}).length > 0 ||
            Object.keys(course.deals || {}).length > 0) && (
            <div
              id="scholarships"
              className="flex flex-col gap-4 md:gap-8 scroll-mt-28 w-full"
            >
              <div className="flex flex-col gap-4 w-full">
                <h3 className="text-lg md:text-4xl font-bold">
                  Awards and Scholarships
                </h3>
                <span className="w-full border-b border-black" />

                <p className="md:text-lg">
                  Click on Scholarship to see est. nett fee
                </p>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {scholarshipArr.map((item, index) => (
                    <div
                      key={`scholarship-${index}`}
                      className={`transition-all duration-300 ${
                        selectedScholarshipCard &&
                        selectedScholarshipCard !== `scholarship-${index}`
                          ? "hidden"
                          : ""
                      }`}
                    >
                      <GeneralCard
                        title={item.title}
                        desc={`MYR ${item.amount.toLocaleString()}`}
                        isSelected={
                          selectedScholarshipCard === `scholarship-${index}`
                        }
                        onCardClick={() =>
                          handleScholarshipCardClick(`scholarship-${index}`)
                        }
                        showDetailsSection={true}
                        detailsContent={
                          <ScholarshipDetail
                            // title={ScholarshipDetailData[index]?.title}
                            // desc={ScholarshipDetailData[index]?.desc}
                            title={item.amount.toLocaleString()}
                            desc={(
                              course.tuitionFee - item.amount
                            ).toLocaleString()}
                          />
                        }
                      />
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex flex-col md:flex-row justify-between gap-2 md:gap-4">
                {totalDeal > 0 && (
                  <div className="flex items-center gap-4 md:w-1/2">
                    <MailOpen className="w-16 h-16" />
                    <div className="text-sm">
                      <p>
                        You could save up to MYR {totalDeal} from our deals and
                        benefits
                      </p>
                      <div>Secure these exclusive offers now!</div>
                    </div>
                  </div>
                )}
                {dealsArr.length > 0 && (
                  <div className="flex flex-col md:w-1/2 shadow-black shadow p-2 md:p-5 rounded">
                    {dealsArr.map((item, index) => (
                      <div
                        key={`deal-${item.title || index}`}
                        className="flex justify-between"
                      >
                        <div>{item.title}</div>
                        <div>MYR {item.amount}</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div className="flex gap-4 justify-between items-center shadow shadow-black p-2 md:p-5 rounded-2xl">
                <HandCoins className="h-16 w-16" />
                <div className="flex flex-col items-center gap-2 md:flex-row w-2/3 md:w-full">
                  <div className="text-sm">
                    <p className="font-semibold">Compare all Scholarships</p>
                    <p>
                      Instantly check the scholarships you qualify for out of
                      172 awards for similar courses with Pathway Match
                    </p>
                  </div>
                  <button className="flex items-center justify-center w-36 h-10 bg-amber-400 text-white rounded ">
                    Start Now
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Course Content */}
          {/* <div className="flex flex-col gap-4 w-full">
          <h3 className="text-lg md:text-4xl font-bold">Course Content</h3>
          <span className="w-full border-b border-black" />
          <CourseContent />
        </div> */}

          {/* What to do next */}
          <div id="consultation" className="flex flex-col gap-4 w-full">
            <h3 className="text-lg md:text-4xl font-bold">
              What can you do next?
            </h3>
            <span className="w-full border-b border-black" />
            <p className="md:text-lg">
              Take action and secure your education today
            </p>
            <div className="flex flex-col md:flex-row justify-between gap-4">
              <Link
                to="/form"
                state={{ formType: "consultation" }}
                className="md:w-1/2 flex gap-4 p-4 items-center shadow shadow-black rounded-2xl hover:scale-105 duration-300"
              >
                <div className="flex flex-col items-center text-center">
                  <LibraryBig className="w-12 h-12" />
                  <p className="text-xl md:text-lg/tight font-bold">
                    Consult Counseller
                  </p>
                </div>
                <div className="flex items-center">
                  <p className="hidden lg:flex text-base/snug">
                    Find out if you are suitable for this course and university
                    with guidance from our experienced counsellor. It's free!
                  </p>
                </div>
              </Link>

              {/* {(course.scholarships.length || course.deals.length) > 0 && ( */}
              {(Object.keys(course.scholarships || {}).length > 0 ||
                Object.keys(course.deals || {}).length > 0) && (
                <button
                  onClick={() => {
                    const el = document.getElementById("scholarships");
                    if (el) el.scrollIntoView({ behavior: "smooth" });
                  }}
                  className="md:w-1/2 flex gap-4 p-4 items-center shadow shadow-black rounded-2xl cursor-pointer hover:scale-105 duration-300"
                >
                  <div className="flex flex-col items-center">
                    <HandCoins className="w-12 h-12" />
                    <p className="text-xl md:text-lg/tight font-bold">
                      Browse Scholarships
                    </p>
                  </div>
                  <div className="flex items-center text-left">
                    <p className="hidden lg:flex text-base/snug">
                      See scholarships that are available for this course and
                      secure the matched offers online
                    </p>
                  </div>
                </button>
              )}
            </div>
          </div>

          {/* Suggested Courses  */}
          {/* <div className="flex flex-col gap-4 w-full">
          <h3 className="text-lg md:text-4xl font-bold">
            Other courses you may be interested
          </h3>
          <span className="w-full border-b border-black" />
        </div> */}
        </div>
      </div>

      <div className="hidden w-1/4 lg:flex flex-col gap-2 h-fit sticky top-28">
        <EnquireSidebar
          fieldOfStudy={course.fieldOfStudy}
          universityName={course.universityName}
        />
        <Sidebar />
      </div>
    </div>
  );
}
